import { useEffect, useState } from 'react'

import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@modules/shadcn/ui/Drawer/Drawer'
import { Input } from '@modules/shadcn/ui/Input/Input'

import type { ThumbnailItem } from '../hooks/useThumbnailGroups'
import {
  countHistoryTitleLength,
  formatHistoryDate,
  HISTORY_TITLE_MAX_LENGTH,
} from '../utils/historyDisplay'
import { isImeComposingKeyEvent } from '../utils/isImeComposingKeyEvent'

interface CustomerTitleSheetProps {
  /** 編集する履歴。nullなら閉じる */
  item: ThumbnailItem | null
  onClose: () => void
  /** 保存（空文字列は未設定に戻す）。失敗は例外で返す */
  onSave: (imageId: number, title: string) => Promise<void> | void
}

/**
 * 施主画面のタイトル入力のボトムシート（決定事項1。モックのCustomerEditTitle）。
 */
export const CustomerTitleSheet: React.FC<CustomerTitleSheetProps> = ({
  item,
  onClose,
  onSave,
}) => {
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setValue(item?.title ?? '')
    setError(null)
  }, [item])

  const length = countHistoryTitleLength(value)
  const tooLong = length > HISTORY_TITLE_MAX_LENGTH

  const handleSave = async () => {
    if (!item?.imageId || tooLong) return
    setSaving(true)
    setError(null)
    try {
      await onSave(item.imageId, value.trim())
      onClose()
    } catch (err) {
      console.error('[CustomerTitleSheet] タイトルの保存に失敗:', err)
      setError('タイトルを保存できませんでした。もう一度お試しください。')
    } finally {
      setSaving(false)
    }
  }

  const meta = item
    ? [
        item.createdBy ? `${item.createdBy}が作成` : undefined,
        // 施主画面なので時刻は出さない（年月日だけ）
        formatHistoryDate(item.createdAt) || undefined,
      ]
        .filter(Boolean)
        .join('・')
    : ''

  return (
    <Drawer onOpenChange={open => (!open && !saving ? onClose() : undefined)} open={item !== null}>
      <DrawerContent className="px-4 pb-6 pt-2" direction="bottom">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-input" />
        <DrawerTitle className="text-base font-bold">この履歴にタイトルを付ける</DrawerTitle>
        <DrawerDescription className="sr-only">
          タイトルは{HISTORY_TITLE_MAX_LENGTH}文字まで入力できます
        </DrawerDescription>
        {item ? (
          <div className="mt-3 flex items-center gap-2.5">
            <div className="h-[66px] w-[88px] shrink-0 overflow-hidden rounded-lg border border-input bg-muted">
              <img alt="" className="h-full w-full object-cover" src={item.url} />
            </div>
            <p className="text-xs text-muted-foreground">{meta}</p>
          </div>
        ) : null}
        <div className="mt-4 flex flex-col gap-1.5">
          <label className="text-xs text-muted-foreground" htmlFor="customer-title-input">
            タイトル
          </label>
          <Input
            aria-invalid={tooLong}
            className="h-12 rounded-[10px] border-2 border-foreground text-base"
            id="customer-title-input"
            onChange={event => setValue(event.target.value)}
            onKeyDown={event => {
              // 日本語変換を確定するEnterでは保存しない（確定後にもう一度Enterで保存）
              if (isImeComposingKeyEvent(event)) return
              if (event.key === 'Enter') {
                event.preventDefault()
                void handleSave()
              }
            }}
            placeholder="例：お父さん案、玄関まわり濃いめ"
            value={value}
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>ご家族で見分けやすい名前がおすすめです</span>
            <span className={cn(tooLong && 'text-destructive')}>
              {length}/{HISTORY_TITLE_MAX_LENGTH}
            </span>
          </div>
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
        <Button
          className="mt-4 h-12 rounded-[10px] text-[15px] font-semibold"
          disabled={saving || tooLong}
          onClick={() => void handleSave()}
          type="button"
        >
          {saving ? '保存中...' : '保存する'}
        </Button>
        <Button
          className="mt-1 h-11 text-sm text-muted-foreground"
          disabled={saving}
          onClick={onClose}
          type="button"
          variant="ghost"
        >
          キャンセル
        </Button>
      </DrawerContent>
    </Drawer>
  )
}
